import { PaginationSearchParams, Uuid } from '@/api/types';
import { CaseResourceType, OrganType } from '@/api/apis/case';
import { AirportType, Hospital, Hub as HubType, TXCenter } from '@/api/apis/facilities';

export enum GroundTripStatus {
  New = 'New',
  Pending = 'Pending',
  Planned = 'Planned',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  Merged = 'Merged',
}

export enum GroundTripServiceType {
  Executive = 'Executive',
  Training = 'Training',
  AdHoc = 'AdHoc',
  NOP = 'NOP',
  TEDS = 'TEDS',
}

export enum PassengerType {
  Employee = 'Employee',
  OcsConsole = 'OcsConsole',
  ExternalPassenger = 'ExternalPassenger',
  Scs = 'Scs',
}

export enum DriverAssignmentStatus {
  WaitingForAoApproval = 'WaitingForAoApproval',
  PendingDriverAssignment = 'PendingDriverAssignment',
  PendingDriverApproval = 'PendingDriverApproval',
  DriverApproved = 'DriverApproved',
  DriverRejected = 'DriverRejected',
}

export enum AoAssignmentStatus {
  NoAoReview = 'NoAoReview',
  PendingAoApproval = 'PendingAoApproval',
  AoApproved = 'AoApproved',
  AoRejected = 'AoRejected',
}

export enum CheckpointModeType {
  PickUp = 'PickUp',
  DropOff = 'DropOff',
  Wait = 'Wait',
}

export enum CheckpointTypeEnum {
  PlannedCheckpointResponse = 'PlannedCheckpointResponse',
  UnplannedCheckpointResponse = 'UnplannedCheckpointResponse',
}

export enum GroundTripLocationType {
  Airport = 'Airport',
  Fbo = 'Fbo',
  Hub = 'Hub',
  DonorHospital = 'DonorHospital',
  TxCenter = 'TxCenter',
  CustomAddress = 'CustomAddress',
}

// Ground Trip Locations
export type GroundTripBaseLocationType = {
  addressLine: string;
  city: string;
  country: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
};

export type GroundTripAirport = {
  locationType: GroundTripLocationType.Airport;
  airportId: Uuid;
} & Pick<AirportType, 'name' | 'iata' | 'icao' | 'maoId'>;

export type GroundTripCustomAddress = {
  locationType: GroundTripLocationType.CustomAddress;
};

export type GroundTripDonorHospital = {
  locationType: GroundTripLocationType.DonorHospital;
  hospitalId: Uuid;
} & Pick<Hospital, 'name' | 'code'>;

export type GroundTripHub = {
  locationType: GroundTripLocationType.Hub;
  hubId: Uuid;
  timezone: string;
} & Pick<HubType, 'name' | 'code'>;

export type GroundTripTxCenter = {
  locationType: GroundTripLocationType.TxCenter;
  txCenterId: Uuid;
} & Pick<TXCenter, 'name' | 'code'>;

export type GroundTripFbo = {
  locationType: GroundTripLocationType.Fbo;
  hubId: Uuid;
  fboName: string;
  airportId: Uuid;
  airportName: string | null;
  airportIata: string | null;
  airportIcao: string | null;
};

export type GroundTripLocationEntity =
  | GroundTripAirport
  | GroundTripCustomAddress
  | GroundTripDonorHospital
  | GroundTripHub
  | GroundTripTxCenter
  | GroundTripFbo;

export type GroundTripLocation = GroundTripLocationEntity & GroundTripBaseLocationType;

// Ground Trip Assignment Types
export type GroundTripVehicle = {
  id: Uuid;
  make: string | null;
  model: string | null;
  licensePlateNumber: string | null;
  type: string | null;
  color: string | null;
};

export type GroundTripDriver = {
  firstName: string;
  lastName: string;
  phone: string;
  imagePath: string | null;
};

export type DriverAssignment = {
  assignmentStatus: DriverAssignmentStatus;
  driver: GroundTripDriver | null;
};

export type GroundTripAoType = {
  name: string;
  phone: string;
};

export type AffiliatedOrganizationAssignment = {
  assignmentStatus: AoAssignmentStatus;
  affiliatedOrganization: GroundTripAoType | null;
};

// Ground Trip Plan
export type DropOffCheckpointType = {
  mode: CheckpointModeType.DropOff;
  dropOffDateTime: string;
  passengerIdsToDrop: Uuid[];
  ianaTimezone: string;
};

export type PickUpCheckpointType = {
  mode: CheckpointModeType.PickUp;
  pickUpDateTime: string;
  passengerIdsToPickUp: Uuid[];
  ianaTimezone: string;
};

export type WaitCheckpointType = {
  mode: CheckpointModeType.Wait;
  waitStartTime: string;
  waitDurationInSeconds: number;
  ianaTimezone: string;
};

export type CheckpointTypeData = DropOffCheckpointType | PickUpCheckpointType | WaitCheckpointType;

export type PlannedGroundTripCheckpointType = {
  $type: CheckpointTypeEnum.PlannedCheckpointResponse;
  checkpointId: Uuid;
  orderIndex: number;
  comment: string | null;
  modeData: CheckpointTypeData;
  isCompleted: boolean;
  locationData: GroundTripLocation;
};

export type UnplannedCheckpointGpsLocationType = {
  latitude: number;
  longitude: number;
  address: string;
};

export type UnplannedGroundTripCheckpointType = {
  $type: CheckpointTypeEnum.UnplannedCheckpointResponse;
  modeData: WaitCheckpointType;
  gpsLocation: UnplannedCheckpointGpsLocationType | null;
  isCompleted: boolean;
};

export type GroundTripCheckpointType = PlannedGroundTripCheckpointType | UnplannedGroundTripCheckpointType;

export type TripPlanType = {
  checkpoints: GroundTripCheckpointType[];
};

// Ground Trip Passengers
export type BaseGroundTripPassenger = {
  passengerId: Uuid;
  passengerType: PassengerType;
  resourceType: CaseResourceType | null;
};

export type GroundTripEmployeePassenger = BaseGroundTripPassenger & {
  passengerType: PassengerType.Employee;
  userId: Uuid;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  profilePhoto: string | null;
  resourceType: CaseResourceType.Employee | CaseResourceType.Surgeon | CaseResourceType.ClinicalSpecialist | null;
};

export type GroundTripOCSDevicePassenger = BaseGroundTripPassenger & {
  passengerType: PassengerType.OcsConsole;
  userId: Uuid;
  organType: OrganType | null;
  fullSerialNumber: string | null;
  logoPath: string | null;
  hubId: string | null;
  resourceType: CaseResourceType.OCSConsole | null;
};

export type GroundTripExternalPassenger = BaseGroundTripPassenger & {
  passengerType: PassengerType.ExternalPassenger;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
};

export type GroundTripScsPassenger = BaseGroundTripPassenger & {
  passengerType: PassengerType.Scs;
};

export type GroundTripPassenger =
  | GroundTripEmployeePassenger
  | GroundTripOCSDevicePassenger
  | GroundTripExternalPassenger
  | GroundTripScsPassenger;

// Ground Trip types
export type GroundTripSpecialOptions = {
  lightsAndSirens: boolean;
  comment: string | null;
};

export type GroundTripMetrics = {
  estimatedDrivingDurationInSeconds: number;
  estimatedWaitingDurationInSeconds: number;
};

export type LinkedLogisticsLegItem = {
  legId: string;
};

export type LinkedLogisticsLegs = {
  caseId: string;
  logisticsId: string;
  routeId: string;
  legs: LinkedLogisticsLegItem[];
};

export type LogisticsGroundTrip = {
  id: Uuid;
  tripNumber: string;
  status: GroundTripStatus;
  serviceType: GroundTripServiceType;
  vehicle: GroundTripVehicle | null;
  driver: DriverAssignment | null;
  affiliatedOrganization: AffiliatedOrganizationAssignment | null;
  tripPlan: TripPlanType;
  passengers: GroundTripPassenger[];
  specialOptions: GroundTripSpecialOptions;
  tripMetrics: GroundTripMetrics | null;
  linkedLogisticsLegs: LinkedLogisticsLegs | null;
  isMergedTrip?: boolean;
};

export type PaginatedGroundTripsQueryParams = PaginationSearchParams & {
  groundTripIds: string;
};
